import { useNavigationParams } from '../navigation/history'
/* THE selection tab — ONE reusable queue component, instantiated PER
 * SURFACE and never across surfaces (W6 REV 3).
 *
 * What it keeps from the old TasksWorkbench, unchanged in behaviour:
 *   · blockers-first ordering on `blocksRecord` semantics, then age
 *   · single-select → a detail panel on the right
 *   · J / K walk, Enter opens, Esc closes
 *   · the `?task=<id>` deep link preselecting a row
 *   · D-3a: per-object chase groups collapse, and the non-blocking
 *     "recommended" chases sit behind a collapsed toggle at the BOTTOM,
 *     so the J/K walk reaches them only after the real work.
 *
 * What REV 3 adds: composable filters, a sort menu, and multi-select
 * with a bulk bar whose verbs are gated to a single task type.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { useListKeys } from '../keyboard/useListKeys'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import type { TaskQueueItem } from '../../lib/tasks'
import { chaseKeyOf, chaseObjectLabelOf, chaseObligationLabelOf } from '../chaseGroups'
import { SURFACE_SLUGS, taskTypeLabel, type Surface } from './placement'
import TaskDetailPanel from './TaskDetailPanel'
import Sentence from '../Sentence'
import { taskSentence } from '../taskSentence'
import {
  AGE_WARN_DAYS,
  SORT_LABELS,
  ageDays,
  applyFilters,
  isRecommended,
  priorityClass,
  sortTasks,
  type SortMode,
} from './queueUtils'
import { EmptyState } from '../state'

type Row =
  | { kind: 'single'; task: TaskQueueItem }
  | { kind: 'group'; key: string; label: string; tasks: TaskQueueItem[] }

/** One row per chase object (order-preserving); 1-item groups collapse
 *  back to plain rows; non-chase items pass through untouched. */
function buildRows(list: TaskQueueItem[]): Row[] {
  const rows: Row[] = []
  const groups = new Map<
    string,
    { kind: 'group'; key: string; label: string; tasks: TaskQueueItem[] }
  >()
  for (const t of list) {
    const key = chaseKeyOf(t)
    if (key) {
      let g = groups.get(key)
      if (!g) {
        g = { kind: 'group', key, label: chaseObjectLabelOf(t) ?? t.fund_name ?? '', tasks: [] }
        groups.set(key, g)
        rows.push(g)
      }
      g.tasks.push(t)
    } else {
      rows.push({ kind: 'single', task: t })
    }
  }
  return rows.map((r) =>
    r.kind === 'group' && r.tasks.length === 1 ? { kind: 'single', task: r.tasks[0] } : r
  )
}

/** Bulk verbs, gated to a single task type. A mixed selection has none. */
interface BulkVerb {
  label: string
  run: (tasks: TaskQueueItem[]) => Promise<void>
}

const BULK_VERBS: Record<string, BulkVerb> = {
  // daily_digest's bulk Acknowledge left with the digest rows (audit cut
  // 1, 2026-09-04) — the rows are no longer officer queue rows.
  request_overdue: {
    label: 'Send reminder',
    run: async (tasks) => {
      for (const t of tasks)
        if (t.target_id)
          await apiFetch(`/api/requests/${t.target_id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'escalated' }),
          })
    },
  },
}

export interface SurfaceQueueProps {
  surface: Surface
  tasks: TaskQueueItem[]
  /** Refetch after a bulk verb or a snooze lands. */
  onChanged?: () => void
}

export default function SurfaceQueue({ surface, tasks, onChanged }: SurfaceQueueProps) {
  const [params, setParams] = useNavigationParams()
  const deepLinkId = params.get('task')

  const [sort, setSort] = useState<SortMode>('priority')
  const [types, setTypes] = useState<Set<string>>(new Set())
  const [urgentHigh, setUrgentHigh] = useState(false)
  const [olderThan7d, setOlderThan7d] = useState(false)
  const selectedId =
    deepLinkId && tasks.some((task) => task.task_id === deepLinkId) ? deepLinkId : null
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [recommendedOpen, setRecommendedOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  // A surface's own filter chips: only the types actually present here.
  const presentTypes = useMemo(() => [...new Set(tasks.map((t) => t.task_type))].sort(), [tasks])

  const filtered = useMemo(
    () => applyFilters(tasks, { types, urgentHigh, olderThan7d }),
    [tasks, types, urgentHigh, olderThan7d]
  )

  const main = useMemo(
    () =>
      sortTasks(
        filtered.filter((t) => !isRecommended(t)),
        sort
      ),
    [filtered, sort]
  )
  const recommended = useMemo(
    () => sortTasks(filtered.filter(isRecommended), sort),
    [filtered, sort]
  )

  /* The J/K walk order IS the display order, and it respects the active
     filters: recommended chases come last, and only once opened. */
  const walk = useMemo(
    () => [...main, ...(recommendedOpen ? recommended : [])],
    [main, recommended, recommendedOpen]
  )

  const select = useCallback(
    (id: string | null, options?: { replace?: boolean }) => {
      const next = new URLSearchParams(params)
      if (id) next.set('task', id)
      else next.delete('task')
      setParams(next, options)
    },
    [params, setParams]
  )

  /* The focus ring FOLLOWS the cursor (keyboard canon, A5): J/K select the
   * row and put the ring on it, so Enter opens the row under the ring and
   * Esc has a row to come back to. */
  const listRef = useRef<HTMLDivElement | null>(null)
  const move = useCallback(
    (delta: number) => {
      if (walk.length === 0) return
      // Focus advances synchronously; URL rendering can lag rapid key repeats.
      const active = document.activeElement
      const focusedId =
        active instanceof HTMLElement && listRef.current?.contains(active)
          ? active.closest<HTMLElement>('[data-task-id]')?.dataset.taskId
          : null
      const idx = walk.findIndex((t) => t.task_id === (focusedId ?? selectedId))
      const next = walk[Math.min(Math.max((idx < 0 ? -1 : idx) + delta, 0), walk.length - 1)]
      if (!next) return
      select(next.task_id, { replace: true })
      listRef.current
        ?.querySelector<HTMLElement>(`[data-testid="wb-queue-row"][data-task-id="${next.task_id}"]`)
        ?.focus()
    },
    [walk, selectedId, select]
  )

  const navigate = useNavigate()
  const selected = walk.find((t) => t.task_id === selectedId) ?? null

  /* OV (ownership-verification) has no useful preview — the pane just
     repeated the row (Ale 2026-09-03). On OV the ROW is primary: a click
     opens the approve screen directly (the old "Open in surface" target),
     and Snooze rides the row itself since the pane is gone. */
  const isOv = surface === 'ownership_verification'
  const openTask = useCallback(
    (id: string) => navigate(`/v2/workbench/${SURFACE_SLUGS[surface]}/task/${id}`),
    [navigate, surface]
  )
  const [snoozingId, setSnoozingId] = useState<string | null>(null)
  const snoozeRow = useCallback(
    async (id: string) => {
      setSnoozingId(id)
      try {
        await apiFetch(`/api/tasks/${id}/snooze`, {
          method: 'POST',
          body: JSON.stringify({ mode: 'next_document' }),
        })
        onChanged?.()
      } catch {
        /* a non-snoozable type answers 409 — leave the row as it was */
      } finally {
        setSnoozingId(null)
      }
    },
    [onChanged]
  )

  /* J / K / Enter / Esc — the keyboard canon's one hook (A5): the walk
   * order is the display order; Enter opens the selected row; Esc closes
   * the detail panel (a typed key never leaves its field). */
  const onNext = useCallback(() => move(1), [move])
  const onPrev = useCallback(() => move(-1), [move])
  const onEnter = useCallback(() => {
    if (selected) openTask(selected.task_id)
  }, [selected, openTask])
  const onEscape = useCallback(() => {
    if (selectedId) select(null)
  }, [selectedId, select])
  useListKeys({ onNext, onPrev, onEnter, onEscape })

  // ── the bulk bar's gate ───────────────────────────────────────────
  const checkedTasks = filtered.filter((t) => checked.has(t.task_id))
  const checkedTypes = new Set(checkedTasks.map((t) => t.task_type))
  const singleType = checkedTypes.size === 1 ? [...checkedTypes][0] : null
  const verb = singleType ? BULK_VERBS[singleType] : undefined

  async function runBulk() {
    if (!verb) return
    setBusy(true)
    try {
      await verb.run(checkedTasks)
      setChecked(new Set())
      onChanged?.()
    } finally {
      setBusy(false)
    }
  }

  const toggleCheck = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const renderRow = (t: TaskQueueItem, inGroup = false) => {
    const days = ageDays(t.created_at) ?? 0
    return (
      <div
        key={t.task_id}
        data-testid="wb-queue-row"
        data-task-type={t.task_type}
        data-task-id={t.task_id}
        className={`qrow${t.task_id === selectedId ? ' sel' : ''}${inGroup ? ' qgroupitem' : ''}${
          days > AGE_WARN_DAYS ? ' qold' : ''
        }`}
        /* reachable by Tab in reading order (keyboard canon, A5): Enter or
           Space does what a click does — and on the row already selected,
           Enter opens it */
        tabIndex={0}
        onClick={() => (isOv ? openTask(t.task_id) : select(t.task_id))}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (isOv || (e.key === 'Enter' && t.task_id === selectedId)) openTask(t.task_id)
            else select(t.task_id)
          }
        }}
      >
        {/* A checkbox only where a bulk verb exists for the type — a
            tickable row whose only bulk outcome is "No bulk action" is
            dead chrome (perception pass 2026-08-31). */}
        {BULK_VERBS[t.task_type] ? (
          <input
            type="checkbox"
            aria-label={`select ${t.title}`}
            data-testid="wb-row-check"
            checked={checked.has(t.task_id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleCheck(t.task_id)}
          />
        ) : null}
        <span className={`pdot ${priorityClass(t)}`} />
        <span className={`pbadge ${priorityClass(t)}`} data-testid="wb-row-priority">
          {t.priority}
        </span>
        {/* SENTENCES, NOT LABELS (Ale 2026-09-02): the row IS a sentence
            carrying its record values as data — fund, object, figures,
            age — instead of title + type/fund/age cells. The engine title
            stays in the DOM (visually hidden) so title-based flows keep
            matching. Inside an expanded chase group the obligation leads.
            S2 (2026-09-03): no age column — the age lives in the sentence;
            past 30 days the row carries `qold` and the age datum warns. */}
        <span className="qtitle" title={t.title}>
          {inGroup ? (
            <b>{chaseObligationLabelOf(t)}</b>
          ) : (
            (() => {
              const s = taskSentence(t)
              return (
                <Sentence
                  className="sn-lead"
                  meaning={s.meaning}
                  values={s.values}
                  fallback={s.fallback}
                  data-testid="wb-row-sentence"
                />
              )
            })()
          )}
          <span className="sr-title">{t.title}</span>
        </span>
        {/* OV rows carry their own Snooze (the preview pane that used to
            hold it is gone) — clicking the row opens the approve screen. */}
        {isOv && !inGroup ? (
          <button
            type="button"
            className="btn"
            data-testid="wb-row-snooze"
            disabled={snoozingId === t.task_id}
            onClick={(e) => {
              e.stopPropagation()
              snoozeRow(t.task_id)
            }}
          >
            Snooze
          </button>
        ) : null}
      </div>
    )
  }

  const renderRows = (list: TaskQueueItem[]) =>
    buildRows(list).map((row) => {
      if (row.kind === 'single') return renderRow(row.task)
      const open = expanded.has(row.key)
      return (
        <div key={`group:${row.key}`} data-testid="wb-chase-group">
          <div
            className="qsec"
            data-testid="wb-chase-group-title"
            onClick={() =>
              setExpanded((prev) => {
                const next = new Set(prev)
                if (next.has(row.key)) next.delete(row.key)
                else next.add(row.key)
                return next
              })
            }
          >
            {open ? '▾' : '▸'}{' '}
            <Sentence
              meaning="chase.group"
              values={{ count: row.tasks.length, object: row.label }}
              fallback={`${row.tasks.length} evidence chases — ${row.label}`}
            />
          </div>
          {open ? row.tasks.map((t) => renderRow(t, true)) : null}
        </div>
      )
    })

  /* The deck surfaces speak through a colored queue bar ("N of M · …");
   * these queues wore bare filter chips beside them — the collage seam
   * of the 2026-08-31 perception pass. Same bar idiom, same counts. */
  const BAR_NOUN: Record<Surface, string> = {
    record_keeping: 'to decide',
    reconciliation: 'to reconcile',
    ownership_verification: 'to verify',
    chase_request: 'to send',
    other_ops: 'open',
  }

  return (
    <>
      <div className={`squeuebar sq-${surface}`} data-testid="wb-queue-bar">
        <span>
          <b>{main.length}</b> {BAR_NOUN[surface]}
          {recommended.length > 0 ? ` · ${recommended.length} recommended` : ''}
        </span>
        <span className="sqhint">J / K walk · Enter opens · Esc closes</span>
      </div>
      <div className="filters" data-testid="wb-filters">
        {presentTypes.map((t) => (
          <button
            type="button"
            key={t}
            data-testid="wb-type-chip"
            data-type={t}
            className={`fchip${types.has(t) ? ' on' : ''}`}
            onClick={() =>
              setTypes((prev) => {
                const next = new Set(prev)
                if (next.has(t)) next.delete(t)
                else next.add(t)
                return next
              })
            }
          >
            {taskTypeLabel(t)}
          </button>
        ))}
        <button
          type="button"
          data-testid="wb-filter-urgent"
          className={`fchip${urgentHigh ? ' on' : ''}`}
          onClick={() => setUrgentHigh((v) => !v)}
        >
          urgent + high
        </button>
        <button
          type="button"
          data-testid="wb-filter-older"
          className={`fchip${olderThan7d ? ' on' : ''}`}
          onClick={() => setOlderThan7d((v) => !v)}
        >
          older than 7 d
        </button>
        <select
          className="sortsel"
          aria-label="sort"
          data-testid="wb-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortMode)}
        >
          {(Object.keys(SORT_LABELS) as SortMode[]).map((m) => (
            <option key={m} value={m}>
              {SORT_LABELS[m]}
            </option>
          ))}
        </select>
        <span className="fcount" data-testid="wb-filtered-count">
          {filtered.length} of {tasks.length}
        </span>
      </div>

      <div className="split">
        <div className="qcol" data-testid={`wb-queue-${surface}`} ref={listRef}>
          {main.length === 0 && recommended.length === 0 ? (
            <EmptyState
              sentence="Nothing open in this queue."
              door={{ label: 'Drop documents ›', to: '/v2/traces' }}
              testId="wb-queue-empty"
            />
          ) : null}
          {renderRows(main)}
          {/* D-3a: recommended (non-blocking) chases behind a collapsed
              toggle, defaulted closed — they never block the record and
              must not crowd the real queue. */}
          {recommended.length > 0 ? (
            <div
              className="qsec"
              data-testid="wb-recommended-toggle"
              onClick={() => setRecommendedOpen((o) => !o)}
            >
              {recommendedOpen ? '▾' : '▸'} Recommended — non-blocking · {recommended.length}
            </div>
          ) : null}
          {recommendedOpen ? renderRows(recommended) : null}
        </div>
        {/* OV opens the approve screen straight from the row — no preview
            pane (Ale 2026-09-03). Every other surface keeps it. */}
        {selected && !isOv ? (
          <TaskDetailPanel task={selected} onClose={() => select(null)} onSnoozed={onChanged} />
        ) : null}
      </div>

      {checked.size > 0 ? (
        <div className="bulkbar" data-testid="wb-bulk-bar">
          <b>{checked.size} selected</b>
          <button
            type="button"
            className="btn primary"
            data-testid="wb-bulk-verb"
            disabled={!verb || busy}
            onClick={runBulk}
          >
            {verb ? verb.label : 'No bulk action'}
          </button>
          <span className="bulknote" data-testid="wb-bulk-note">
            {singleType === null
              ? 'Select one type at a time — a bulk verb only means one thing.'
              : verb
                ? `${taskTypeLabel(singleType)}`
                : `No bulk action for ${taskTypeLabel(singleType)}.`}
          </span>
          <button type="button" className="btn" onClick={() => setChecked(new Set())}>
            Clear
          </button>
        </div>
      ) : null}
    </>
  )
}
